/**
 * Returns panorama window object
 * @returns { Panorama }
 */
const getPanorama = () => {
    try {
        // First check local window
        if (typeof window.panorama === "function") {
            return window.panorama;
        }
        // In isolated AWT widget panorama exists under window[Symbol.for("panorama")]
        const panoramaSymbol = Symbol.for("panorama");
        // Check window[panoramaSymbol]
        if (typeof window[panoramaSymbol] === "function") {
            return window[panoramaSymbol];
        }
        // Check window.top safely
        if (window.top && window.self !== window.top) {
            try {
                // This might throw SecurityError in cross-origin scenarios
                if (typeof window.top.panorama === "function") {
                    return window.top.panorama;
                }
            }
            catch (crossOriginError) {
                // no-op
            }
        }
    }
    catch (e) {
        // no-op
    }
    return undefined;
};
/**
 * Checks if Panorama is enabled in current page
 * @returns { boolean }
 */
export const isPanoramaEnabled = () => {
    return !!getPanorama();
};
/**
 * Checks if Panorama is enabled and empties existing metric buffer
 */
const checkIfCanEmptyBuffer = () => {
    if (isPanoramaEnabled()) {
        initialBuffer.forEach(metric => {
            emitPanoramaCustomEvent(metric);
        });
    }
};
// Maintain metrics until Panorama is enabled on the page
const initialBuffer = [];
/**
 * Push the metrics to internal buffer until Panorama is enabled
 * @param { Metric } metric
 */
const pushToBuffer = (metric) => {
    initialBuffer.push(metric);
    checkIfCanEmptyBuffer();
};
/**
 * Emits Panorama custom event
 * @param { Metric } metric
 */
export const emitPanoramaCustomEvent = (metric) => {
    if (!isPanoramaEnabled()) {
        pushToBuffer(Object.assign(Object.assign({}, metric), { timestamp: Date.now() }));
        return;
    }
    const panoramaWindow = getPanorama();
    if (panoramaWindow) {
        panoramaWindow("trackCustomEvent", Object.assign(Object.assign({}, metric), { timestamp: metric.timestamp || Date.now() }));
    }
};
export const emitFunnelInteractionEvent = (metric) => {
    return emitPanoramaCustomEvent(Object.assign(Object.assign({}, metric), { eventType: "funnelInteraction" }));
};
export const emitPerformanceMetric = (metric) => {
    return emitPanoramaCustomEvent(Object.assign(Object.assign({}, metric), { eventType: "performance", eventSource: "polaroid" }));
};
export const emitComponentRenderedMetric = (metric) => {
    return emitPanoramaCustomEvent(Object.assign(Object.assign({}, metric), { eventType: "componentRendered" }));
};
export const emitComponentUpdatedMetric = (metric) => {
    return emitPanoramaCustomEvent(Object.assign(Object.assign({}, metric), { eventType: "componentUpdated" }));
};
export const withErrorHandling = (fnName) => (func) => {
    return (...args) => {
        try {
            return func(...args);
        }
        catch (err) {
            try {
                emitPanoramaCustomEvent({
                    eventContext: fnName,
                    eventSource: "panoramaInternal",
                    eventDetail: err ? err.toString() : "",
                });
            }
            catch (_a) {
                // Do nothing
            }
        }
    };
};
/**
 * Sets funnel interaction ID in Panorama tracker
 * @param { string } id
 */
export const setFunnelInteractionId = (id) => {
    const panoramaWindow = getPanorama();
    if (panoramaWindow) {
        panoramaWindow("setFunnelInteractionId", id);
    }
};
